import React from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Badge, 
  Card, 
  CardHeader, 
  CardBody, 
  Accordion, 
  AccordionItem, 
  AccordionButton, 
  AccordionPanel, 
  AccordionIcon,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';

const CodingAnalysisPanel = ({ results, claimsData, claimType }) => {
  if (!results) return null;

  return (
    <VStack spacing={6} align="stretch" mt={6}>
      {/* Line Level Pointers */}
      <Card>
        <CardHeader>
          <Heading size="md">Line Level Pointers (Box 24E)</Heading>
        </CardHeader>
        <CardBody>
          {results.line_level_pointers && Object.keys(results.line_level_pointers).length > 0 ? (
            <VStack align="stretch" spacing={3}>
              {Object.entries(results.line_level_pointers).map(([cpt, pointers]) => (
                <HStack key={cpt}>
                  <Text fontWeight="bold" width="150px">
                    {cpt === 'ALL_CPTS' ? 'All IONM CPTs:' : `CPT ${cpt}:`}
                  </Text>
                  <HStack>
                    {pointers.map(ptr => (
                      <Badge key={ptr} colorScheme="blue" fontSize="md" px={2}>{ptr}</Badge>
                    ))}
                    {pointers.length === 0 && <Text color="gray.500" fontSize="sm">None</Text>}
                  </HStack>
                </HStack>
              ))}
            </VStack>
          ) : (
            <Text color="gray.500">No line level pointers available.</Text>
          )}
        </CardBody>
      </Card>

      {/* Original vs Adjusted CPT Codes Card */}
      {claimsData && claimsData[claimType] && (
        <Accordion allowToggle>
          <AccordionItem border="none">
            <Card borderLeft="4px solid" borderColor="teal.400">
              <CardHeader p={0}>
                <AccordionButton _hover={{ bg: 'teal.50' }} p={4}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="teal.700">Original vs Adjusted CPT Codes</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </CardHeader>
              <AccordionPanel pb={4}>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={6}>
                    {/* Quality Check Discrepancies */}
                    {results?.quality_check?.status === 'needs_review' && (
                      <Box mb={4} p={4} bg="red.50" rounded="md" borderWidth="1px" borderColor="red.200">
                        <Text fontWeight="bold" color="red.800" mb={2}>🚨 Quality Check Discrepancies</Text>
                        <Box fontSize="sm" color="red.900" whiteSpace="pre-wrap">
                          <ReactMarkdown>{results.quality_check.notes}</ReactMarkdown>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Original Codes Table */}
                    {claimsData[claimType].original_cpt_codes && claimsData[claimType].original_cpt_codes.length > 0 && (
                      <Box>
                        <Heading size="sm" mb={2}>Original CPT Codes</Heading>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Code</Th>
                              <Th>Units</Th>
                              <Th>Modifiers</Th>
                              <Th>Status</Th>
                              <Th>Justification</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {claimsData[claimType].original_cpt_codes.map((code, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="bold">{code.cpt_code}</Td>
                                <Td>{code.units}</Td>
                                <Td>{code.modifiers ? code.modifiers.join(', ') : code.modifier}</Td>
                                <Td>
                                  <Badge colorScheme={code.status === 'final' ? 'green' : 'yellow'}>
                                    {code.status}
                                  </Badge>
                                </Td>
                                <Td fontSize="xs" whiteSpace="normal">{code.justification}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}

                    {/* Adjusted Codes Table */}
                    <Box>
                      <Heading size="sm" mb={2}>Adjusted CPT Codes</Heading>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Code</Th>
                            <Th>Units</Th>
                            <Th>Modifiers</Th>
                            <Th>Status</Th>
                            <Th>Justification</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {claimsData[claimType].cpt_codes && claimsData[claimType].cpt_codes.map((code, idx) => (
                            <Tr key={idx}>
                              <Td fontWeight="bold">{code.cpt_code}</Td>
                              <Td>{code.units}</Td>
                              <Td>{code.modifiers ? code.modifiers.join(', ') : code.modifier}</Td>
                              <Td>
                                <Badge colorScheme={code.status === 'final' ? 'green' : 'yellow'}>
                                  {code.status}
                                </Badge>
                              </Td>
                              <Td fontSize="xs" whiteSpace="normal">{code.justification}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>

                    {/* Adjustment Log */}
                    {claimsData[claimType].adjustment_log && claimsData[claimType].adjustment_log.length > 0 && (
                      <Box>
                        <Heading size="sm" mb={2}>Adjustment Log</Heading>
                        <List spacing={2}>
                          {claimsData[claimType].adjustment_log.map((log, idx) => (
                            <ListItem key={idx} fontSize="sm">
                              <ListIcon as={InfoIcon} color="blue.400" />
                              {typeof log === 'string' ? log : JSON.stringify(log)}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </AccordionPanel>
            </Card>
          </AccordionItem>
        </Accordion>
      )}
    </VStack>
  );
};

export default CodingAnalysisPanel;
